import * as Api from '@ViewModels';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import type { CreateScenarioInput, Scenario, UpdateScenarioInput } from '../../voice-agent/types/scenarios';
import { extractArrayFromResponse } from '../utils';

const SCENARIOS_QUERY_KEY = 'scenarios';

export function useScenarios({ enabled = true }: { enabled?: boolean } = {}) {
	const userSession = useUserSession();
	return useQuery({
		enabled,
		queryFn: async () => {
			const response = await userSession.webServiceHelper.callWebServiceAsync<{
				value?: Scenario[];
				values?: Scenario[];
			}>(Api.ImpersonationBroker.composeApiUrl({ urlPath: 'aida/scenarios' }), 'GET');
			return extractArrayFromResponse<Scenario>(response.value);
		},
		queryKey: [SCENARIOS_QUERY_KEY],
	});
}

/**
 * Fetch the scenarios available to the current user (role-filtered server-side)
 */
export function useMyScenarios({ enabled = true }: { enabled?: boolean } = {}) {
	const userSession = useUserSession();
	return useQuery({
		enabled,
		queryFn: async () => {
			const response = await userSession.webServiceHelper.callWebServiceAsync<{
				value?: Scenario[];
				values?: Scenario[];
			}>(Api.ImpersonationBroker.composeApiUrl({ urlPath: 'aida/scenarios/my-scenarios' }), 'GET');
			return extractArrayFromResponse<Scenario>(response.value);
		},
		queryKey: [SCENARIOS_QUERY_KEY, 'my-scenarios'],
	});
}

/**
 * Fetch a single scenario by ID
 */
export function useScenario({ id, enabled = true }: { id: string | null; enabled?: boolean }) {
	const userSession = useUserSession();
	return useQuery({
		enabled: enabled && !!id,
		queryFn: async () => {
			const response = await userSession.webServiceHelper.callWebServiceAsync<Scenario>(
				Api.ImpersonationBroker.composeApiUrl({ urlPath: `aida/scenarios/${id}` }),
				'GET'
			);
			return response.value;
		},
		queryKey: [SCENARIOS_QUERY_KEY, id],
	});
}

/**
 * Create a new scenario
 */
export function useCreateScenarioMutation() {
	const userSession = useUserSession();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (input: CreateScenarioInput): Promise<Scenario> => {
			const response = await userSession.webServiceHelper.callWebServiceAsync<Scenario>(
				Api.ImpersonationBroker.composeApiUrl({ urlPath: 'aida/scenarios' }),
				'POST',
				input
			);

			if (!response.value) {
				throw new Error('No data returned from create scenario API');
			}

			return response.value;
		},
		onError: (error: Error) => {
			console.error('Failed to create scenario:', error);
		},
		onSuccess: () => {
			// Invalidate all scenario queries to refresh data
			queryClient.invalidateQueries({ queryKey: [SCENARIOS_QUERY_KEY] });
		},
	});
}

/**
 * Update an existing scenario
 */
export function useUpdateScenarioMutation() {
	const userSession = useUserSession();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({ id, input }: { id: string; input: UpdateScenarioInput }): Promise<Scenario> => {
			if (!id) {
				throw new Error('Scenario ID is required');
			}

			const response = await userSession.webServiceHelper.callWebServiceAsync<Scenario>(
				Api.ImpersonationBroker.composeApiUrl({ urlPath: `aida/scenarios/${id}` }),
				'PUT',
				input
			);

			if (!response.value) {
				throw new Error('No data returned from update scenario API');
			}

			return response.value;
		},
		onError: (error: Error) => {
			console.error('Failed to update scenario:', error);
		},
		onSuccess: () => {
			// Invalidate all scenario queries to refresh data
			queryClient.invalidateQueries({ queryKey: [SCENARIOS_QUERY_KEY] });
		},
	});
}

/**
 * Delete a scenario
 */
export function useDeleteScenarioMutation() {
	const userSession = useUserSession();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (id: string): Promise<void> => {
			if (!id) {
				throw new Error('Scenario ID is required');
			}

			await userSession.webServiceHelper.callWebServiceAsync(
				Api.ImpersonationBroker.composeApiUrl({ urlPath: `aida/scenarios/${id}` }),
				'DELETE'
			);
		},
		onError: (error: Error) => {
			console.error('Failed to delete scenario:', error);
		},
		onSuccess: () => {
			// Invalidate all scenario queries to refresh data
			queryClient.invalidateQueries({ queryKey: [SCENARIOS_QUERY_KEY] });
		},
	});
}
